import { useSelector } from 'react-redux';
import { State } from 'redux/initialState';
import { FC, createContext, useContext, useMemo } from 'react';

import { Partner } from 'types';
import dividendsTemplates from 'constants/dividendsTemplates';

import { PartnerDividendsContextType } from '../types/PartnerDividendsContextType';
import { partnerDividendsInitValue } from '../constants/partnerDividendsInitValue';
import { useGetDividends } from '../hooks/useGetDividends';
import { useGetDocuments } from '../../../../_shared/hooks/useGetDocuments';

const PartnerDividendsContext = createContext<PartnerDividendsContextType>(
  partnerDividendsInitValue
);

export const PartnerDividendsProvider: FC<{
  children: React.ReactNode; // eslint-disable-line
  partner: Partner;
  showTitle?: boolean;
  isSummaryView?: boolean;
}> = ({ children, partner, showTitle = true, isSummaryView = false }) => {
  const actualSociety = useSelector(
    (state: State) => state.society.actualSociety
  );

  const societyTemplate = actualSociety?.templates?.dividends;

  const templateId =
    societyTemplate ||
    dividendsTemplates[process.env.NODE_ENV as keyof typeof dividendsTemplates];

  const documentIds = useMemo(() => [templateId], [templateId]);

  const { data, isLoading } = useGetDividends({ partner });

  const { data: documentsData, isLoading: isDocumentsLoading } =
    useGetDocuments({
      documentIds,
    });

  return (
    <PartnerDividendsContext.Provider
      value={{
        data: {
          dividends: data.dividends,
          totalDividendsAmount: data.totalDividendsAmount,
          dividendsTemplateDocument: documentsData.documents?.length
            ? documentsData.documents[0]
            : null,
          showTitle,
          isSummaryView,
          isLoading: isLoading || isDocumentsLoading,
        },
      }}
    >
      {children}
    </PartnerDividendsContext.Provider>
  );
};

export const usePartnerDividends = () => {
  const context = useContext(PartnerDividendsContext);
  if (!context) {
    throw new Error(
      'usePartnerDividends must be used within a PartnerDividendsProvider'
    );
  }
  return context;
};
